import { useState, useCallback } from 'react';
import { Upload, ChevronRight, ChevronLeft, Loader2, FileSpreadsheet, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';

interface VendorImportWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImport: (vendors: VendorImportRow[]) => Promise<void>;
}

export interface VendorImportRow {
  name: string;
  city?: string;
  phone?: string;
  email?: string;
  status?: string;
  address?: string;
  state?: string;
  country?: string;
}

const VENDOR_FIELDS = [
  { key: 'name', label: 'Vendor Name', required: true },
  { key: 'address', label: 'Address', required: false },
  { key: 'city', label: 'City', required: false },
  { key: 'state', label: 'State', required: false },
  { key: 'country', label: 'Country', required: false },
  { key: 'phone', label: 'Phone', required: false },
  { key: 'email', label: 'Email', required: false },
  { key: 'status', label: 'Status', required: false },
];

type Step = 'upload' | 'mapping' | 'preview';

export default function VendorImportWizard({ open, onOpenChange, onImport }: VendorImportWizardProps) {
  const [step, setStep] = useState<Step>('upload');
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvData, setCsvData] = useState<string[][]>([]);
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});
  const [isImporting, setIsImporting] = useState(false);
  const [fileName, setFileName] = useState<string>('');

  const resetWizard = useCallback(() => {
    setStep('upload');
    setCsvHeaders([]);
    setCsvData([]);
    setColumnMapping({});
    setFileName('');
  }, []);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      resetWizard();
    }
    onOpenChange(newOpen);
  };

  const parseCSV = (text: string): { headers: string[]; rows: string[][] } => {
    const lines = text.split(/\r?\n/).filter((line) => line.trim());
    if (lines.length === 0) return { headers: [], rows: [] };

    const parseRow = (row: string): string[] => {
      const result: string[] = [];
      let current = '';
      let inQuotes = false;

      for (let i = 0; i < row.length; i++) {
        const char = row[i];
        if (char === '"') {
          if (inQuotes && row[i + 1] === '"') {
            current += '"';
            i++;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      result.push(current.trim());
      return result;
    };

    const headers = parseRow(lines[0]);
    const rows = lines.slice(1).map(parseRow);

    return { headers, rows };
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const { headers, rows } = parseCSV(text);
      
      setCsvHeaders(headers);
      setCsvData(rows);

      // Auto-map columns based on similar names
      const autoMapping: Record<string, string> = {};
      headers.forEach((header) => {
        const headerLower = header.toLowerCase().replace(/[^a-z]/g, '');
        VENDOR_FIELDS.forEach((field) => {
          const fieldLower = field.label.toLowerCase().replace(/[^a-z]/g, '');
          const keyLower = field.key.toLowerCase();
          if (headerLower.includes(fieldLower) || headerLower.includes(keyLower) || fieldLower.includes(headerLower)) {
            if (!autoMapping[field.key]) {
              autoMapping[field.key] = header;
            }
          }
        });
      });
      setColumnMapping(autoMapping);
      setStep('mapping');
    };
    reader.readAsText(file);
  };

  const handleMappingChange = (fieldKey: string, csvHeader: string) => {
    setColumnMapping((prev) => ({
      ...prev,
      [fieldKey]: csvHeader === '__none__' ? '' : csvHeader,
    }));
  };

  const getMappedData = (): VendorImportRow[] => {
    return csvData.map((row) => {
      const vendor: VendorImportRow = { name: '' };
      VENDOR_FIELDS.forEach((field) => {
        const csvHeader = columnMapping[field.key];
        if (csvHeader) {
          const headerIndex = csvHeaders.indexOf(csvHeader);
          if (headerIndex !== -1) {
            (vendor as any)[field.key] = row[headerIndex] || '';
          }
        }
      });
      return vendor;
    }).filter((v) => v.name.trim() !== '');
  };

  const handleImport = async () => {
    setIsImporting(true);
    try {
      const vendors = getMappedData();
      await onImport(vendors);
      handleOpenChange(false);
    } finally {
      setIsImporting(false);
    }
  };

  const isNameMapped = !!columnMapping.name;
  const mappedData = step === 'preview' ? getMappedData() : [];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Import Vendors</DialogTitle>
        </DialogHeader>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 py-4 border-b">
          {(['upload', 'mapping', 'preview'] as Step[]).map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step === s
                    ? 'bg-primary text-primary-foreground'
                    : ['upload', 'mapping', 'preview'].indexOf(step) > i
                    ? 'bg-primary/20 text-primary'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {['upload', 'mapping', 'preview'].indexOf(step) > i ? (
                  <Check className="w-4 h-4" />
                ) : (
                  i + 1
                )}
              </div>
              <span className="text-sm hidden sm:inline">
                {s === 'upload' ? 'Upload' : s === 'mapping' ? 'Map Columns' : 'Preview'}
              </span>
              {i < 2 && <ChevronRight className="w-4 h-4 text-muted-foreground" />}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="flex-1 overflow-auto py-4">
          {step === 'upload' && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <FileSpreadsheet className="w-10 h-10 text-primary" />
              </div>
              <h3 className="text-lg font-medium mb-2">Upload CSV File</h3>
              <p className="text-sm text-muted-foreground mb-6 text-center max-w-sm">
                Select a CSV file with vendor data. The first row should contain column headers.
              </p>
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <div className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">
                  <Upload className="w-5 h-5" />
                  Choose File
                </div>
              </label>
            </div>
          )}

          {step === 'mapping' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg mb-4">
                <FileSpreadsheet className="w-5 h-5 text-muted-foreground" />
                <span className="text-sm">{fileName}</span>
                <span className="text-sm text-muted-foreground">
                  ({csvData.length} rows, {csvHeaders.length} columns)
                </span>
              </div>

              <p className="text-sm text-muted-foreground">
                Map the columns from your CSV file to the vendor fields below.
              </p>

              <div className="space-y-3">
                {VENDOR_FIELDS.map((field) => (
                  <div key={field.key} className="grid grid-cols-2 gap-4 items-center">
                    <Label className="flex items-center gap-1">
                      {field.label}
                      {field.required && <span className="text-destructive">*</span>}
                    </Label>
                    <Select
                      value={columnMapping[field.key] || '__none__'}
                      onValueChange={(value) => handleMappingChange(field.key, value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select column" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">-- Not mapped --</SelectItem>
                        {csvHeaders.map((header) => (
                          <SelectItem key={header} value={header}>
                            {header}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>

              {!isNameMapped && (
                <p className="text-sm text-destructive mt-4">
                  Vendor Name is required. Please map a column to it.
                </p>
              )}
            </div>
          )}

          {step === 'preview' && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Review the data below before importing. {mappedData.length} vendors will be imported.
              </p>

              <ScrollArea className="h-[300px] border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-muted sticky top-0">
                    <tr>
                      <th className="text-left p-2 font-medium">Name</th>
                      <th className="text-left p-2 font-medium">City</th>
                      <th className="text-left p-2 font-medium">State</th>
                      <th className="text-left p-2 font-medium">Phone</th>
                      <th className="text-left p-2 font-medium">Email</th>
                      <th className="text-left p-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mappedData.slice(0, 50).map((vendor, i) => (
                      <tr key={i} className="border-t">
                        <td className="p-2">{vendor.name}</td>
                        <td className="p-2">{vendor.city || '—'}</td>
                        <td className="p-2">{vendor.state || '—'}</td>
                        <td className="p-2">{vendor.phone || '—'}</td>
                        <td className="p-2">{vendor.email || '—'}</td>
                        <td className="p-2">{vendor.status || 'active'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollArea>

              {mappedData.length > 50 && (
                <p className="text-sm text-muted-foreground">
                  Showing first 50 of {mappedData.length} vendors.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => {
              if (step === 'mapping') setStep('upload');
              else if (step === 'preview') setStep('mapping');
            }}
            disabled={step === 'upload'}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>

            {step === 'mapping' && (
              <Button onClick={() => setStep('preview')} disabled={!isNameMapped}>
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}

            {step === 'preview' && (
              <Button onClick={handleImport} disabled={isImporting || mappedData.length === 0}>
                {isImporting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    Import {mappedData.length} Vendors
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
